import { Icon } from "@iconify/react";
import { useState, useEffect } from "react";
import ServiceRadioGroup from "./ServiceRadioGroup";
import { Button, DatePicker, Input, Textarea } from "../common";
import BusinessTimeRadioGroup from "./BusinessTimeRadioGroup";
import { formatDateToShortUTC, formatMonthDay } from "../../utils/format";
import PaymentMethodRadioGroup from "./PaymentMethodRadioGroup";
import CreditCardForm from "./CreditCardForm";
import { processPayment } from "../../services/paymentService";
import { createBooking } from "../../services/bookingService";

const BookAppointmentModal = ({
  businessName,
  businessId,
  services = [],
  times = [],
  isOpen = false,
  onClose,
}) => {
  const [step, setStep] = useState(1);
  const [isVisible, setIsVisible] = useState(false);
  const [activedServiceIndex, setActivedServiceIndex] = useState(0);
  const [activedTimeIndex, setActivedTimeIndex] = useState(0);
  const [date, setDate] = useState(new Date());
  const [clientInfo, setClientInfo] = useState({
    fullName: "",
    email: "",
    phone: "",
    message: "",
  });
  const [activedPaymentMethodIndex, setActivedPaymentMethodIndex] = useState(0);
  const [isPaymentSuccess, setIsPaymentSuccess] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState("");
  const [contentKey, setContentKey] = useState(1);
  const [isAnimating, setIsAnimating] = useState(false);
  const [paymentSuccessVisible, setPaymentSuccessVisible] = useState(false);
  const [direction, setDirection] = useState("forward");
  const [shouldRender, setShouldRender] = useState(false);

  // Payment states
  const [cardData, setCardData] = useState({
    cardNumber: "",
    expiry: "",
    cvv: "",
    cardholderName: "",
  });
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [cardErrors, setCardErrors] = useState({});
  const [paymentResult, setPaymentResult] = useState(null);

  useEffect(() => {
    if (isOpen) {
      // Reset visibility state when opening
      setShouldRender(true);
      setIsVisible(false);
      setPaymentSuccessVisible(false);
      setIsPaymentSuccess(false);
      setStep(1);
      setContentKey(1);
      setIsAnimating(false);
      // Reset payment states
      setCardData({
        cardNumber: "",
        expiry: "",
        cvv: "",
        cardholderName: "",
      });
      setPaymentError("");
      setCardErrors({});
      setPaymentResult(null);
      setIsProcessingPayment(false);
      // Use double requestAnimationFrame to ensure browser has painted initial state
      // This ensures consistent smooth animations every time
      let rafId1;
      let rafId2;

      rafId1 = requestAnimationFrame(() => {
        rafId2 = requestAnimationFrame(() => {
          setIsVisible(true);
        });
      });

      // Cleanup: cancel both RAFs if component unmounts or isOpen changes
      return () => {
        if (rafId1) cancelAnimationFrame(rafId1);
        if (rafId2) cancelAnimationFrame(rafId2);
      };
    } else {
      // Start closing animation
      setIsVisible(false);
      setPaymentSuccessVisible(false);
      // Delay unmounting to allow closing animation to complete
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300); // Match the transition duration
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!shouldRender) return null;

  const handleContinue = () => {
    setDirection("forward");
    setIsAnimating(true);
    setTimeout(() => {
      setStep(step + 1);
      setContentKey(step + 1);
      setIsAnimating(false);
    }, 200);
  };

  const handleBack = () => {
    setDirection("backward");
    setIsAnimating(true);
    setTimeout(() => {
      setStep(step - 1);
      setContentKey(step - 1);
      setIsAnimating(false);
    }, 200);
  };

  const validateCardData = () => {
    const errors = {};

    // Validate card number (remove spaces for validation)
    const cardNumber = cardData.cardNumber.replace(/\s/g, "");
    if (!cardNumber || cardNumber.length < 13 || cardNumber.length > 19) {
      errors.cardNumber = "Please enter a valid card number";
    }

    // Validate expiry
    const expiryParts = cardData.expiry.split("/");
    if (!cardData.expiry || expiryParts.length !== 2) {
      errors.expiry = "Please enter a valid expiry date (MM/YY)";
    } else {
      const month = parseInt(expiryParts[0]);
      const year = parseInt("20" + expiryParts[1]);
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1;

      if (month < 1 || month > 12) {
        errors.expiry = "Please enter a valid month (01-12)";
      } else if (
        year < currentYear ||
        (year === currentYear && month < currentMonth)
      ) {
        errors.expiry = "Card has expired";
      }
    }

    // Validate CVV
    if (!cardData.cvv || cardData.cvv.length < 3) {
      errors.cvv = "Please enter a valid CVV";
    }

    // Validate cardholder name
    if (!cardData.cardholderName || cardData.cardholderName.trim().length < 2) {
      errors.cardholderName = "Please enter cardholder name";
    }

    setCardErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePay = async () => {
    setIsAnimating(true);
    setTimeout(() => {
      setIsPaymentSuccess(true);
      setIsAnimating(false);
      setIsProcessingPayment(false);
      // Trigger payment success animation
      setTimeout(() => {
        setPaymentSuccessVisible(true);
      }, 50);
    }, 150);
    // // If "Pay at Venue" is selected (index 1), skip payment processing
    // if (activedPaymentMethodIndex === 1) {
    //   await handlePayAtVenue();
    //   return;
    // }

    // // Validate credit card data
    // if (!validateCardData()) {
    //   setPaymentError("Please fill in all required card details correctly");
    //   return;
    // }

    // setIsProcessingPayment(true);
    // setPaymentError("");

    // try {
    //   const service = services[activedServiceIndex];
    //   const expiryParts = cardData.expiry.split("/");

    //   // Prepare payment data
    //   const paymentData = {
    //     amount: service.price.toString(),
    //     currency: "ILS",
    //     cardDetails: {
    //       cardNumber: cardData.cardNumber.replace(/\s/g, ""),
    //       expiryMonth: expiryParts[0].padStart(2, "0"),
    //       expiryYear: expiryParts[1],
    //       cvv: cardData.cvv,
    //       cardholderName: cardData.cardholderName,
    //     },
    //     customerInfo: {
    //       fullName: clientInfo.fullName,
    //       email: clientInfo.email,
    //       phone: clientInfo.phone,
    //     },
    //     bookingInfo: {
    //       serviceName: service.name,
    //       date: date.toISOString(),
    //       time: times[activedTimeIndex],
    //       businessName: businessName,
    //     },
    //   };

    //   // Process payment through PayMe.io
    //   const paymentResult = await processPayment(paymentData);

    //   if (!paymentResult.success) {
    //     setPaymentError(
    //       paymentResult.error || "Payment failed. Please try again."
    //     );
    //     setIsProcessingPayment(false);
    //     return;
    //   }

    //   // Create booking with payment info
    //   const bookingResult = await createBooking({
    //     businessId: businessId || "unknown",
    //     serviceId: service.id || activedServiceIndex.toString(),
    //     serviceName: service.name,
    //     date: date,
    //     time: times[activedTimeIndex],
    //     clientInfo: clientInfo,
    //     paymentInfo: {
    //       transactionId: paymentResult.transactionId,
    //       confirmationCode: paymentResult.confirmationCode,
    //       paymentMethod: "credit_card",
    //       cardLastFour: paymentResult.cardLastFour,
    //     },
    //   });

    //   if (!bookingResult.success) {
    //     setPaymentError(
    //       "Payment processed but failed to create booking. Please contact support."
    //     );
    //     setIsProcessingPayment(false);
    //     return;
    //   }

    //   // Success - show success screen
    //   setPaymentResult({
    //     confirmationCode:
    //       paymentResult.confirmationCode || bookingResult.confirmationCode,
    //     cardLastFour: paymentResult.cardLastFour,
    //     transactionId: paymentResult.transactionId,
    //   });
    //   setConfirmationCode(
    //     paymentResult.confirmationCode || bookingResult.confirmationCode
    //   );

    //   setIsAnimating(true);
    //   setTimeout(() => {
    //     setIsPaymentSuccess(true);
    //     setIsAnimating(false);
    //     setIsProcessingPayment(false);
    //     // Trigger payment success animation
    //     setTimeout(() => {
    //       setPaymentSuccessVisible(true);
    //     }, 50);
    //   }, 150);
    // } catch (error) {
    //   console.error("Payment processing error:", error);
    //   setPaymentError(
    //     error.message || "An unexpected error occurred. Please try again."
    //   );
    //   setIsProcessingPayment(false);
    // }
  };

  const handlePayAtVenue = async () => {
    setIsProcessingPayment(true);
    setPaymentError("");

    try {
      const service = services[activedServiceIndex];

      // Create booking without payment
      const bookingResult = await createBooking({
        businessId: businessId || "unknown",
        serviceId: service.id || activedServiceIndex.toString(),
        serviceName: service.name,
        date: date,
        time: times[activedTimeIndex],
        clientInfo: clientInfo,
        paymentInfo: {
          paymentMethod: "pay_at_venue",
        },
      });

      if (!bookingResult.success) {
        setPaymentError("Failed to create booking. Please try again.");
        setIsProcessingPayment(false);
        return;
      }

      // Success - show success screen
      setPaymentResult({
        confirmationCode: bookingResult.confirmationCode,
        cardLastFour: null,
        transactionId: null,
      });
      setConfirmationCode(bookingResult.confirmationCode);

      setIsAnimating(true);
      setTimeout(() => {
        setIsPaymentSuccess(true);
        setIsAnimating(false);
        setIsProcessingPayment(false);
        // Trigger payment success animation
        setTimeout(() => {
          setPaymentSuccessVisible(true);
        }, 50);
      }, 150);
    } catch (error) {
      console.error("Booking creation error:", error);
      setPaymentError(
        error.message || "An unexpected error occurred. Please try again."
      );
      setIsProcessingPayment(false);
    }
  };

  const renderHeader = () => (
    <div className="p-6 border-b border-[#2734771A] flex-shrink-0">
      <div className="w-full flex flex-row items-start justify-between">
        <div className="">
          <h2 className="text-2xl font-semibold">Book Appointment</h2>
          <p className="text-dark-blue mt-1">{businessName}</p>
        </div>
        <button className="mt-1" onClick={onClose}>
          <Icon icon="mdi:times" className="w-5 h-5" />
        </button>
      </div>
      {!isPaymentSuccess && (
        <div className="mt-6">
          <Stepper steps={4} activedStep={step} />
        </div>
      )}
    </div>
  );

  const renderContent = () => (
    <div className="px-6 pt-6 pb-5 relative">
      {paymentError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600 text-sm">{paymentError}</p>
        </div>
      )}
      {!isPaymentSuccess ? (
        <div
          key={contentKey}
          className={`transition-all duration-300 ease-in-out ${
            isAnimating
              ? direction === "forward"
                ? "opacity-0 translate-x-4"
                : "opacity-0 -translate-x-4"
              : "opacity-100 translate-x-0"
          }`}
        >
          {step === 1 && (
            <Step1
              services={services}
              activedServiceIndex={activedServiceIndex}
              setActivedServiceIndex={setActivedServiceIndex}
            />
          )}

          {step === 2 && (
            <Step2
              times={times}
              activedTimeIndex={activedTimeIndex}
              setActivedTimeIndex={setActivedTimeIndex}
              date={date}
              setDate={setDate}
              onBack={handleBack}
              onContinue={handleContinue}
            />
          )}

          {step === 3 && (
            <Step3
              clientInfo={clientInfo}
              setClientInfo={setClientInfo}
              onBack={handleBack}
              onContinue={handleContinue}
            />
          )}

          {step === 4 && (
            <Step4
              service={services[activedServiceIndex]}
              date={date}
              time={times[activedTimeIndex]}
              activedPaymentMethodIndex={activedPaymentMethodIndex}
              setActivedPaymentMethodIndex={setActivedPaymentMethodIndex}
              cardData={cardData}
              setCardData={setCardData}
              cardErrors={cardErrors}
            />
          )}
        </div>
      ) : (
        <div
          className={`transition-all duration-500 ease-out ${
            paymentSuccessVisible
              ? "opacity-100 translate-y-0 scale-100"
              : "opacity-0 translate-y-4 scale-95"
          }`}
        >
          <PaymentSuccess
            confirmationCode={confirmationCode}
            businessName={businessName}
            service={services[activedServiceIndex]}
            date={date}
            time={times[activedTimeIndex]}
            paidAmount={services[activedServiceIndex].price}
            paymentLastNumber={paymentResult?.cardLastFour || "2745"}
            paymentMethod={
              activedPaymentMethodIndex === 1 ? "pay_at_venue" : "credit_card"
            }
          />
        </div>
      )}
    </div>
  );

  const renderFooter = () => (
    <div className="w-full flex flex-row items-center gap-2 px-6 pb-[30px] flex-shrink-0 border-t border-[#2734771A] pt-4">
      {!isPaymentSuccess ? (
        <>
          {step > 1 && (
            <Button
              variant="white"
              label="Back"
              className="w-1/2 rounded-[10px] py-2 font-semibold"
              onClick={handleBack}
            />
          )}
          {step < 4 && (
            <Button
              variant="primary-gradient"
              label="Continue"
              className={`${
                step === 1 ? "w-full" : "w-1/2"
              } rounded-[10px] py-2 font-semibold`}
              onClick={handleContinue}
            />
          )}
          {step === 4 && (
            <Button
              variant="primary-gradient"
              label={
                isProcessingPayment
                  ? "Processing..."
                  : activedPaymentMethodIndex === 1
                  ? "Confirm Booking"
                  : `Pay ₪${services[activedServiceIndex].price}`
              }
              className="w-1/2 rounded-[10px] py-2 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handlePay}
              disabled={isProcessingPayment}
            />
          )}
        </>
      ) : (
        <Button
          variant="primary-gradient"
          label="Check Mailbox"
          icon="mdi:email-outline"
          className="w-full rounded-[10px] py-2 font-semibold"
          onClick={() => window.open("https://mail.google.com", "_blank")}
        />
      )}
    </div>
  );

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 transition-opacity duration-300 ease-out p-4 ${
        isVisible ? "opacity-100" : "opacity-0"
      }`}
      onClick={onClose}
    >
      <div
        className={`lg:w-[670px] w-full max-w-[90vw] bg-white rounded-3xl overflow-hidden transition-all duration-300 ease-out flex flex-col max-h-[90vh] ${
          isVisible
            ? "scale-100 translate-y-0 opacity-100"
            : "scale-95 translate-y-4 opacity-0"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {renderHeader()}
        {/* Content - Scrollable */}
        <div className="overflow-y-auto flex-1 min-h-0">{renderContent()}</div>
        {/* Footer */}
        {renderFooter()}
      </div>
    </div>
  );
};

const Stepper = ({ steps = 4, activedStep = 1 }) => {
  return (
    <div className="grid grid-cols-4 gap-2">
      {Array.from({ length: steps }).map((_, index) => {
        const isActive = activedStep >= index + 1;
        return (
          <div
            key={index}
            className="w-full h-2 rounded-full bg-[#E6EDFF] overflow-hidden relative"
          >
            <div
              className={`h-full rounded-full transition-all duration-500 ease-out ${
                isActive ? "bg-primary w-full" : "bg-[#E6EDFF] w-0"
              }`}
              style={{
                transitionDelay: `${index * 100}ms`,
              }}
            ></div>
          </div>
        );
      })}
    </div>
  );
};

const Step1 = ({ services, activedServiceIndex, setActivedServiceIndex }) => {
  return (
    <>
      <h3 className="text-lg font-semibold mb-3">Select a service</h3>
      <ServiceRadioGroup
        items={services}
        activedIndex={activedServiceIndex}
        onSelect={setActivedServiceIndex}
      />
    </>
  );
};

const Step2 = ({
  times = [],
  activedTimeIndex,
  setActivedTimeIndex,
  date,
  setDate,
}) => {
  return (
    <div className="flex flex-col gap-[15px]">
      <DatePicker
        label="Select a date"
        placeholder="Select a date"
        value={date}
        onChange={setDate}
      />

      <div className="">
        <label className="text-sm font-medium mb-2">Select a time</label>

        <BusinessTimeRadioGroup
          times={times}
          activedIndex={activedTimeIndex}
          onSelect={setActivedTimeIndex}
        />
      </div>
    </div>
  );
};

const Step3 = ({ clientInfo, setClientInfo }) => {
  return (
    <div className="flex flex-col gap-5">
      <Input
        label="Full Name"
        placeholder="Jhon"
        icon="mdi:user-outline"
        value={clientInfo.fullName}
        onChange={(value) => setClientInfo({ ...clientInfo, fullName: value })}
      />
      <Input
        label="Email"
        placeholder="example@gmail.com"
        icon="mdi:email-outline"
        value={clientInfo.email}
        onChange={(value) => setClientInfo({ ...clientInfo, email: value })}
      />
      <Input
        label="Phone"
        placeholder="059-123-4567"
        icon="mdi:phone-outline"
        value={clientInfo.phone}
        onChange={(value) => setClientInfo({ ...clientInfo, phone: value })}
      />
      <Textarea
        label="Special Requests (Optional)"
        placeholder="Any special requests or notes..."
        value={clientInfo.message}
        onChange={(value) => setClientInfo({ ...clientInfo, message: value })}
      />
    </div>
  );
};

const Step4 = ({
  service,
  date,
  time,
  activedPaymentMethodIndex,
  setActivedPaymentMethodIndex,
  cardData,
  setCardData,
  cardErrors,
}) => {
  const paymentMethods = [
    {
      name: "Credit Card",
      icon: "mdi:credit-card-outline",
    },
    {
      name: "Pay at Venue",
      icon: null,
    },
  ];

  return (
    <div className="flex flex-col gap-[25px]">
      <div className="bg-[#F1F5FF] border border-[#1B51EC33] rounded-2xl pt-[17px] px-[17px] flex flex-col gap-3">
        <h2 className="font-semibold">Booking Summary</h2>

        <div className="flex flex-row items-center justify-between text-sm">
          <span className="text-dark-blue">Service:</span>
          <span className="font-bold">{service.name}</span>
        </div>

        <div className="flex flex-row items-center justify-between text-sm">
          <span className="text-dark-blue">Date:</span>
          <span className="font-bold">{formatDateToShortUTC(date)}</span>
        </div>

        <div className="flex flex-row items-center justify-between text-sm">
          <span className="text-dark-blue">Time:</span>
          <span className="font-bold">{time}</span>
        </div>

        <div className="flex flex-row items-center justify-between text-sm border-t border-[#1B51EC33] py-2">
          <span className="font-bold">Total:</span>
          <span className="font-bold text-lg text-primary">
            ₪{service.price}
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <h3 className="text-sm">Choose Payment Method</h3>
        <PaymentMethodRadioGroup
          items={paymentMethods}
          activedIndex={activedPaymentMethodIndex}
          onSelect={setActivedPaymentMethodIndex}
        />
      </div>

      {activedPaymentMethodIndex === 0 && (
        <div className="p-[17px] rounded-[16px] border border-[#1B51EC33]">
          <CreditCardForm
            cardData={cardData}
            setCardData={setCardData}
            errors={cardErrors}
          />
        </div>
      )}

      {activedPaymentMethodIndex === 1 && (
        <div className="p-[17px] rounded-[16px] border border-[#1B51EC33] bg-yellow-50">
          <p className="text-sm text-dark-blue">
            You will pay at the venue when you arrive. Your booking will be
            confirmed once you complete this form.
          </p>
        </div>
      )}
    </div>
  );
};

const PaymentSuccess = ({
  confirmationCode,
  businessName,
  service,
  date,
  time,
  paidAmount,
  paymentLastNumber,
  paymentMethod,
}) => {
  return (
    <div className="flex flex-col items-center justify-center gap-4">
      <img src="/icons/confirmed.svg" alt="confirmed" className="w-20 h-20" />

      <div className="flex flex-col items-center justify-center gap-2">
        <h3 className="text-2xl font-bold">Booking Confirmed!</h3>
        <p className="text-dark-blue">
          Payment successful! We've sent a confirmation email.
        </p>
      </div>

      <div className="p-[25px] border border-[#1B51EC33] bg-[#F1F5FF] rounded-2xl w-full flex flex-col gap-3">
        <div className="flex flex-col items-center justify-center">
          <p className="text-sm text-dark-blue">Booking Confirmation Code</p>
          <p className="text-[30px] font-bold">{confirmationCode}</p>
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex flex-row items-center justify-between text-sm">
            <span className="text-dark-blue">Business:</span>
            <span className="font-bold">{businessName}</span>
          </div>

          <div className="flex flex-row items-center justify-between text-sm">
            <span className="text-dark-blue">Service:</span>
            <span className="font-bold">{service.name}</span>
          </div>

          <div className="flex flex-row items-center justify-between text-sm">
            <span className="text-dark-blue">Date & Time:</span>
            <span className="font-bold">
              {formatMonthDay(date)} at {time}
            </span>
          </div>

          <div className=" border-t border-[#1B51EC33] flex flex-col gap-2 pt-2">
            <div className="flex flex-row items-center justify-between text-sm">
              <span>Amount Paid:</span>
              <span className="font-bold text-primary">₪{paidAmount}</span>
            </div>

            <div className="flex flex-row items-center justify-between text-sm">
              <span>Payment Method:</span>
              <span className="font-bold">
                {paymentMethod === "pay_at_venue"
                  ? "Pay at Venue"
                  : `••••${paymentLastNumber}`}
              </span>
            </div>
          </div>
        </div>
      </div>

      <p className="text-sm text-dark-blue text-center">
        Check your email for booking details and calendar invite.
      </p>
    </div>
  );
};

export default BookAppointmentModal;
